import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { yupResolver } from '@hookform/resolvers/yup';
import { data as allCurrencies } from 'currency-codes';
import { isNil } from 'lodash-es';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import {
  MdDeleteOutline,
  MdOutlineEdit,
  MdRadioButtonChecked,
  MdRadioButtonUnchecked,
} from 'react-icons/md';
import Select from 'react-select';
import { number, object, string } from 'yup';
import { BABYLON_MIN_SERVICE_DURATION_MINUTES } from '../../../../constants/Constants';
import useActionModal from '../../../../hooks/useActionModal';
import useAppNavigation from '../../../../hooks/useAppNavigation';
import {
  deletePartnerServiceRequest,
  getPartnerDetailsRequest,
  getPartnerServiceDetailsRequest,
  switchPartnerServiceRequest,
  updatePartnerServiceRequest,
} from '../../../../redux/ducks/DashboardDucks';
import type {
  GetPartnerDetailsResponseDataType,
  GetPartnerServiceDetailsResponseDataType,
  UpdatePartnerServiceRequestType,
} from '../../../../redux/ducks/DashboardDucks.types';
import { setDetailsOnEdit } from '../../../../redux/ducks/UIDucks';
import { ActionModalActionType } from '../../../../redux/ducks/UIDucks.types';
import type { RootReducerType } from '../../../../redux/reducers/RootReducer.types';
import Button from '../../../button/Button';
import Input from '../../../inputs/Input';
import TextArea from '../../../inputs/TextArea';
import {
  TAG_DESCRIPTORS,
  type TAG_DESCRIPTOR_TYPE,
} from '../../../layout/CategoriesBar/CategoriesDescriptors';
import Spinner from '../../../layout/Spinner/Spinner';
import DashboardSectionContainer from '../DashboardSectionContainer';
import DashboardSectionDetailsContainer from '../DashboardSectionDetailsContainer';

interface ServiceDetailsSectionProps {
  partnerDetails: GetPartnerDetailsResponseDataType;
}

export type CurrencyType = {
  id: string;
  label: string;
  value: string;
};

export type TagType = {
  id: string;
  label: string;
  value: TAG_DESCRIPTOR_TYPE;
};

const formattedCurrencies: CurrencyType[] = allCurrencies.map((cc: any) => ({
  id: cc.code,
  label: cc.code,
  value: cc.code,
}));

type EditServiceInput = {
  name: string;
  description?: string;
  duration: number;
  price: number;
};

const ServiceDetailsSection: React.FC<ServiceDetailsSectionProps> = ({
  partnerDetails,
}) => {
  const { t, i18n } = useTranslation();
  const { navigate } = useAppNavigation();
  const { serviceId } = useParams();
  const dispatch = useDispatch();
  const [onInit, setOnInit] = useState<boolean>(false);
  const [onSwitchService, setOnSwitchService] = useState<boolean>(false);
  const actionModal = useActionModal();
  const [onDeleteService, setOnDeleteService] = useState<boolean>(false);
  const [updatedBusinesses, setUpdatedBusinesses] = useState<
    string[] | undefined
  >();
  const [onUpdateService, setOnUpdateService] = useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState<TagType | undefined>();
  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyType | undefined
  >();

  const [serviceDetails, setServiceDetails] = useState<
    GetPartnerServiceDetailsResponseDataType | undefined
  >();
  const {
    onActionModalConfirm,
    isDetailsOnEdit,
    onDetailsActionSave,
    onDetailsActionCancel,
  } = useSelector((state: RootReducerType) => state.ui);

  const invalidTagSelection = useMemo<boolean>(
    () => isNil(selectedTag),
    [selectedTag],
  );

  const {
    getPartnerServiceDetailsResponse,
    deletePartnerServiceResponse,
    updatePartnerServiceResponse,
    switchPartnerServiceResponse,
  } = useSelector((state: RootReducerType) => state.dashboard);

  const formSchema = object().shape({
    name: string()
      .required(
        t(
          'static.dashboard.service_details_section.validation_step_service_error_name_required',
        ),
      )
      .min(
        2,
        t(
          'static.dashboard.service_details_section.validation_step_service_error_name_short',
        ),
      )
      .max(
        50,
        t(
          'static.dashboard.service_details_section.validation_step_service_error_name_long',
        ),
      ),
    description: string()
      .trim()
      .optional()
      .transform((v, o) => (o === '' ? undefined : v))
      .min(
        2,
        t(
          'static.dashboard.service_details_section.validation_step_service_error_description_short',
        ),
      )
      .max(
        300,
        t(
          'static.dashboard.service_details_section.validation_step_service_error_description_long',
        ),
      ),
    duration: number()
      .required(
        t(
          'static.dashboard.service_details_section.validation_step_service_error_duration_required',
        ),
      )
      .typeError(
        t(
          'static.dashboard.service_details_section.validation_step_service_error_duration_number',
        ),
      )
      .min(
        BABYLON_MIN_SERVICE_DURATION_MINUTES,
        t(
          'static.dashboard.service_details_section.validation_step_service_error_duration_minimum',
          { minutes: BABYLON_MIN_SERVICE_DURATION_MINUTES },
        ),
      ),
    price: number()
      .required(
        t(
          'static.dashboard.service_details_section.validation_step_service_error_price_required',
        ),
      )
      .typeError(
        t(
          'static.dashboard.service_details_section.validation_step_service_error_price_number',
        ),
      )
      .min(
        0,
        t(
          'static.dashboard.service_details_section.validation_step_service_error_price_positive',
        ),
      ),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<EditServiceInput>({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
  });

  const configTags = useSelector(
    (state: RootReducerType) => state.config.appConfig?.tags,
  );

  const availableTags = useMemo<TagType[]>(() => {
    if (isNil(configTags) || configTags.length <= 0) {
      return [];
    }

    const result = [...configTags]
      .filter(
        (configTag) =>
          TAG_DESCRIPTORS.filter((tg) => tg.id === configTag.id).length > 0,
      )
      .map((configTag) => {
        const desc = TAG_DESCRIPTORS.filter((tg) => tg.id === configTag.id)[0];
        return {
          id: desc.id,
          label: t(`static.tags.${desc.id}`),
          value: desc,
        };
      });

    return result;
  }, [configTags]);

  useEffect(() => {
    if (
      !isNil(getPartnerServiceDetailsResponse) &&
      !isNil(getPartnerServiceDetailsResponse?.data)
    ) {
      setServiceDetails(getPartnerServiceDetailsResponse.data);
      setValue('name', getPartnerServiceDetailsResponse.data.name || '');
      setValue(
        'duration',
        Math.round(getPartnerServiceDetailsResponse.data.duration / 60),
      );
      setValue('price', getPartnerServiceDetailsResponse.data.price);
      setValue(
        'description',
        getPartnerServiceDetailsResponse.data?.description || '',
      );
      const formattedCurs = formattedCurrencies.filter(
        (c) => c.id === getPartnerServiceDetailsResponse.data?.currency,
      );
      if (formattedCurs.length > 0) {
        setSelectedCurrency(formattedCurs[0]);
      }
      const setTag: TagType | undefined = !isNil(
        getPartnerServiceDetailsResponse.data?.tag,
      )
        ? availableTags.filter(
            (at) => at.id === getPartnerServiceDetailsResponse.data?.tag,
          )[0]
        : undefined;
      setSelectedTag(setTag);
      setOnInit(false);
    }
  }, [getPartnerServiceDetailsResponse, onInit]);

  useEffect(() => {
    if (serviceId) {
      dispatch(
        getPartnerServiceDetailsRequest({
          userPartner: partnerDetails.id,
          serviceId,
        }),
      );
      setOnInit(true);
    }

    return () => {
      setOnInit(false);
      dispatch(setDetailsOnEdit(false));
      setServiceDetails(undefined);
      setSelectedCurrency(undefined);
      setSelectedTag(undefined);
      setUpdatedBusinesses(undefined);
      setOnSwitchService(false);
      setOnDeleteService(false);
      setOnUpdateService(false);
    };
  }, []);

  useEffect(() => {
    if (onUpdateService && !isNil(updatePartnerServiceResponse)) {
      if (updatePartnerServiceResponse?.error) {
        setOnUpdateService(false);
        toast.error(
          t(
            'static.dashboard.service_details_section.service_update_error_notification_text',
          ),
        );
      } else if (updatePartnerServiceResponse?.data) {
        setOnUpdateService(false);
        dispatch(setDetailsOnEdit(false));
        toast.success(
          t(
            'static.dashboard.service_details_section.service_updated_notification_text',
          ),
        );
        setServiceDetails(undefined);
        dispatch(
          getPartnerServiceDetailsRequest({
            userPartner: partnerDetails.id,
            serviceId: updatePartnerServiceResponse.data.id,
          }),
        );
      }
    }
  }, [updatePartnerServiceResponse]);

  const onModify = () => {
    dispatch(setDetailsOnEdit(true));
  };

  const onDisableCall = (disabled: boolean) => {
    if (serviceDetails) {
      if (disabled) {
        actionModal.onOpen({
          action: ActionModalActionType.DisableService,
          message: t(
            'static.dashboard.service_details_section.disable_service_dialog_text',
            {
              name: serviceDetails.name,
            },
          ),
          cancelLabel: t(
            'static.dashboard.service_details_section.disable_service_cancel_btn_label',
          ),
          confirmLabel: t(
            'static.dashboard.service_details_section.disable_service_confirm_btn_label',
          ),
          confirmLabelType: 'reject',
        });
      } else {
        actionModal.onOpen({
          action: ActionModalActionType.EnableService,
          message: t(
            'static.dashboard.service_details_section.enable_service_dialog_text',
            {
              name: serviceDetails.name,
            },
          ),
          cancelLabel: t(
            'static.dashboard.service_details_section.enable_service_cancel_btn_label',
          ),
          confirmLabel: t(
            'static.dashboard.service_details_section.enable_service_confirm_btn_label',
          ),
          confirmLabelType: 'success',
        });
      }
    }
  };

  const onDeleteCall = () => {
    if (serviceDetails) {
      actionModal.onOpen({
        action: ActionModalActionType.DeleteService,
        message: t(
          'static.dashboard.service_details_section.delete_service_dialog_text',
          {
            name: serviceDetails.name,
          },
        ),
        cancelLabel: t(
          'static.dashboard.service_details_section.delete_service_cancel_btn_label',
        ),
        confirmLabel: t(
          'static.dashboard.service_details_section.delete_service_confirm_btn_label',
        ),
        confirmLabelType: 'reject',
      });
    }
  };

  const onCancel = useCallback(() => {
    dispatch(setDetailsOnEdit(false));
    setUpdatedBusinesses(undefined);
    setSelectedCurrency(undefined);
    setSelectedTag(undefined);
    reset();
    if (serviceDetails) {
      setValue('name', serviceDetails.name || '');
      setValue('duration', Math.round(serviceDetails.duration / 60));
      setValue('price', serviceDetails.price);
      setValue('description', serviceDetails?.description || '');
      const formattedCurs = formattedCurrencies.filter(
        (c) => c.id === serviceDetails?.currency,
      );
      if (formattedCurs.length > 0) {
        setSelectedCurrency(formattedCurs[0]);
      }
    }
  }, [dispatch, reset, serviceDetails, setValue]);

  const onBusinessCheck = (businessId: string) => {
    if (serviceDetails) {
      if (!isNil(updatedBusinesses)) {
        setUpdatedBusinesses([
          ...updatedBusinesses.filter((b) => b !== businessId),
          businessId,
        ]);
      } else {
        setUpdatedBusinesses([
          ...serviceDetails.businesses
            .map((biz) => biz.id)
            .filter((b) => b !== businessId),
          businessId,
        ]);
      }
    }
  };

  const onBusinessUncheck = (businessId: string) => {
    if (serviceDetails) {
      if (!isNil(updatedBusinesses)) {
        setUpdatedBusinesses([
          ...updatedBusinesses.filter((b) => b !== businessId),
        ]);
      } else {
        setUpdatedBusinesses([
          ...serviceDetails.businesses
            .map((biz) => biz.id)
            .filter((b) => b !== businessId),
        ]);
      }
    }
  };

  const onSave: SubmitHandler<EditServiceInput> = useCallback(
    (data) => {
      trigger()
        .then((success) => {
          if (success && serviceId) {
            const updatedService: UpdatePartnerServiceRequestType = {
              name: data.name,
              price: data.price,
              duration: data.duration * 60,
              userPartner: partnerDetails.id,
              id: serviceId,
              currency: selectedCurrency?.label ?? 'USD',
            };
            if (!isNil(data?.description)) {
              updatedService.description = data.description;
            } else {
              updatedService.description = '';
            }
            if (!isNil(selectedTag)) {
              updatedService.tag = selectedTag.id;
            }
            if (!isNil(updatedBusinesses)) {
              updatedService.businesses = updatedBusinesses;
            }

            setOnUpdateService(true);
            setUpdatedBusinesses(undefined);
            dispatch(updatePartnerServiceRequest(updatedService));
          }
        })
        .catch((e: any) => console.error(e));
    },
    [
      dispatch,
      partnerDetails.id,
      selectedCurrency?.label,
      selectedTag,
      serviceId,
      trigger,
      updatedBusinesses,
    ],
  );

  useEffect(() => {
    if (isDetailsOnEdit) {
      if (onDetailsActionSave) {
        handleSubmit(onSave)();
      } else if (onDetailsActionCancel) {
        onCancel();
      }
    }
  }, [
    onDetailsActionSave,
    onDetailsActionCancel,
    isDetailsOnEdit,
    onSave,
    onCancel,
    handleSubmit,
  ]);

  const createSelectCurrencyLabel = (option: any) => (
    <div className="flex flex-row items-center gap-3">
      <div>{option.label}</div>
    </div>
  );

  const createSelectCategoryLabel = (tag: TagType) => (
    <div className="grid grid-cols-1 items-start justify-start gap-1">
      <div className="flex w-full flex-row items-start justify-start">
        <div className="mr-1 flex flex-col items-center justify-center">
          <tag.value.icon size={20} />
        </div>
        <div className="col-span-8 flex grow flex-row items-start justify-start">
          <div className="line-clamp-1 overflow-hidden text-ellipsis break-all">
            <span className="ml-1 capitalize text-neutral-500">
              {tag.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (actionModal.isOpen && onActionModalConfirm) {
      if (serviceDetails) {
        if (actionModal.currentAction === ActionModalActionType.DeleteService) {
          dispatch(
            deletePartnerServiceRequest({
              userPartner: partnerDetails.id,
              id: serviceDetails.id,
            }),
          );
          setOnDeleteService(true);
        } else if (
          actionModal.currentAction === ActionModalActionType.DisableService
        ) {
          // todo: dispatch update with service disabled
          setOnSwitchService(true);
          dispatch(
            switchPartnerServiceRequest({
              userPartner: partnerDetails.id,
              id: serviceDetails.id,
            }),
          );
        } else if (
          actionModal.currentAction === ActionModalActionType.EnableService
        ) {
          // todo: dispatch update with service enabled
          setOnSwitchService(true);
          dispatch(
            switchPartnerServiceRequest({
              userPartner: partnerDetails.id,
              id: serviceDetails.id,
            }),
          );
        }
      }
    }
  }, [onActionModalConfirm]);

  useEffect(() => {
    if (deletePartnerServiceResponse && onDeleteService) {
      setOnDeleteService(false);

      if (!isNil(deletePartnerServiceResponse?.data)) {
        actionModal.onClose();
        toast.success(
          t(
            'static.dashboard.service_details_section.service_deleted_notification_text',
          ),
        );
        dispatch(getPartnerDetailsRequest({ userPartner: partnerDetails.id }));
        navigate(-1);
      } else {
        toast.error(
          t(
            'static.dashboard.service_details_section.service_delete_error_notification_text',
          ),
        );
      }
    }
  }, [deletePartnerServiceResponse]);

  useEffect(() => {
    if (switchPartnerServiceResponse && onSwitchService) {
      setOnSwitchService(false);

      if (!isNil(switchPartnerServiceResponse?.data)) {
        actionModal.onClose();
        toast.success(
          t(
            serviceDetails?.active
              ? 'static.dashboard.service_details_section.service_switched_inactive_notification_text'
              : 'static.dashboard.service_details_section.service_switched_active_notification_text',
          ),
        );
        dispatch(getPartnerDetailsRequest({ userPartner: partnerDetails.id }));
        navigate(-1);
      } else {
        toast.error(
          t(
            serviceDetails?.active
              ? 'static.dashboard.service_details_section.service_switch_inactive_error_notification_text'
              : 'static.dashboard.service_details_section.service_switch_active_error_notification_text',
          ),
        );
      }
    }
  }, [switchPartnerServiceResponse]);

  return serviceDetails ? (
    <DashboardSectionDetailsContainer>
      <div className="relative flex size-full min-w-full flex-col items-center justify-start">
        <div className="mb-12 flex min-w-full grow flex-col items-start justify-start gap-5 py-4 px-3">
          <div className="flex min-w-full flex-col items-start justify-start">
            {isDetailsOnEdit ? (
              <span className="min-w-full grow py-1 text-start">
                <Input
                  id="name"
                  label={t(
                    'static.dashboard.service_details_section.name_label',
                  )}
                  disabled={onUpdateService}
                  register={register}
                  errors={errors}
                />
              </span>
            ) : (
              <>
                <span className="text-lg font-bold drop-shadow-sm">
                  {t('static.dashboard.service_details_section.name_label')}
                </span>
                <button
                  id="name_on_edit"
                  key="name_on_edit"
                  aria-label="name_on_edit"
                  data-test-id="name_on_edit"
                  className="grow cursor-pointer text-start text-lg capitalize"
                  type="button"
                  onClick={onModify}
                >
                  {serviceDetails.name}
                </button>
              </>
            )}
          </div>

          <div className="flex min-w-full flex-col items-start justify-start">
            {isDetailsOnEdit ? (
              <span className="min-w-full grow py-1 text-start">
                <Input
                  id="duration"
                  label={t(
                    'static.dashboard.service_details_section.duration_label',
                  )}
                  disabled={onUpdateService}
                  register={register}
                  errors={errors}
                />
              </span>
            ) : (
              <>
                <span className="text-lg font-bold drop-shadow-sm">
                  {t('static.dashboard.service_details_section.duration_label')}
                </span>
                <button
                  id="duration_on_edit"
                  key="duration_on_edit"
                  aria-label="duration_on_edit"
                  data-test-id="duration_on_edit"
                  className="grow cursor-pointer text-start text-lg"
                  type="button"
                  onClick={onModify}
                >
                  {t(
                    'static.dashboard.service_details_section.service_duration_label',
                    {
                      duration: Math.floor(serviceDetails.duration / 60),
                    },
                  )}
                </button>
              </>
            )}
          </div>

          <div className="flex min-w-full flex-col items-start justify-start">
            {isDetailsOnEdit ? (
              <span className="flex min-w-full flex-row gap-3">
                <span className="grow">
                  <Input
                    id="price"
                    label={t(
                      'static.dashboard.service_details_section.price_label',
                    )}
                    disabled={onUpdateService}
                    register={register}
                    errors={errors}
                    type="number"
                  />
                </span>
                <span>
                  <Select
                    data-test-id="select-currency"
                    placeholder={
                      selectedCurrency
                        ? selectedCurrency.label
                        : t('static.tags.search_placeholder')
                    }
                    isSearchable={false}
                    isDisabled={onUpdateService}
                    menuPlacement="auto"
                    menuPosition="fixed"
                    menuShouldScrollIntoView
                    closeMenuOnSelect
                    className="rounded-md border-2"
                    options={formattedCurrencies}
                    value={selectedCurrency}
                    onChange={(newCurrency) => {
                      setSelectedCurrency(
                        formattedCurrencies.filter(
                          (fc) => fc.id === newCurrency?.id,
                        )[0],
                      );
                    }}
                    formatOptionLabel={createSelectCurrencyLabel}
                    classNames={{
                      control: () => 'px-3 py-1 border-2',
                      input: () => 'text-lg',
                      option: () => 'text-lg',
                    }}
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 6,
                      colors: {
                        ...theme.colors,
                        primary: '#083344',
                        primary25: '#ecfeff',
                      },
                    })}
                  />
                </span>
              </span>
            ) : (
              <>
                <span className="text-lg font-bold drop-shadow-sm">
                  {t('static.dashboard.service_details_section.price_label')}
                </span>
                <span className="text-lg">
                  {new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency: serviceDetails.currency,
                  }).format(serviceDetails.price)}
                </span>
              </>
            )}
          </div>

          <div className="flex min-w-full flex-col items-start justify-start">
            {isDetailsOnEdit ? (
              <span className="min-w-full grow py-1 text-start">
                <TextArea
                  id="description"
                  label={t(
                    'static.dashboard.service_details_section.description_label',
                  )}
                  disabled={onUpdateService}
                  register={register}
                  getValues={getValues}
                  setValue={setValue}
                  errors={errors}
                  maxLength={300}
                />
              </span>
            ) : (
              <>
                <span className="text-lg font-bold drop-shadow-sm">
                  {t(
                    'static.dashboard.service_details_section.description_label',
                  )}
                </span>
                <span className="text-lg">
                  {serviceDetails?.description
                    ? serviceDetails?.description
                    : '-'}
                </span>
              </>
            )}
          </div>

          <div className="flex min-w-full flex-col items-start justify-start">
            {isDetailsOnEdit ? (
              <span className="flex min-w-full grow flex-col items-start justify-center gap-2">
                <span
                  className={`text-lg ${
                    invalidTagSelection ? 'text-red-500' : 'text-slate-400'
                  }`}
                >
                  {t('static.dashboard.service_details_section.tags_label')}
                </span>
                <span
                  className={
                    invalidTagSelection
                      ? 'min-w-full rounded-md border border-red-500'
                      : 'min-w-full rounded-md'
                  }
                >
                  <Select
                    data-test-id="select-tags"
                    placeholder={
                      selectedTag
                        ? t(`static.tags.${selectedTag.id}`)
                        : undefined
                    }
                    isSearchable
                    isDisabled={onUpdateService}
                    menuPlacement="auto"
                    menuPosition="fixed"
                    menuShouldScrollIntoView
                    closeMenuOnSelect
                    className="rounded-md border-2"
                    options={availableTags}
                    value={selectedTag}
                    onChange={(newTag) => {
                      setSelectedTag(
                        availableTags.filter((at) => at.id === newTag?.id)[0],
                      );
                    }}
                    formatOptionLabel={createSelectCategoryLabel}
                    classNames={{
                      control: () => 'px-3 py-1 border-2',
                      input: () => 'text-lg',
                      option: () => 'text-lg',
                    }}
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 6,
                      colors: {
                        ...theme.colors,
                        primary: '#083344',
                        primary25: '#ecfeff',
                      },
                    })}
                  />
                </span>
              </span>
            ) : (
              <>
                <span className="text-lg font-bold drop-shadow-sm">
                  {t('static.dashboard.service_details_section.tags_label')}
                </span>
                <span className="min-w-full">
                  {!isNil(selectedTag) ? (
                    <span className="flex min-w-full flex-row items-start justify-center gap-2">
                      <span className="hidden">
                        <selectedTag.value.icon size={20} />
                      </span>
                      <span className="grow text-left text-lg capitalize">
                        {t(`static.tags.${selectedTag.id}`)}
                      </span>
                    </span>
                  ) : (
                    <span className="text-lg">-</span>
                  )}
                </span>
              </>
            )}
          </div>

          <div className="flex min-w-full flex-col items-start justify-start">
            {isDetailsOnEdit ? (
              <>
                <span className="text-lg text-slate-400 drop-shadow-sm">
                  {t(
                    'static.dashboard.service_details_section.businesses_label',
                  )}
                </span>
                <span className="min-w-full">
                  {partnerDetails.businesses.length === 0 && (
                    <span className="min-w-full text-center font-semibold text-slate-500">
                      {t(
                        'static.dashboard.service_details_section.no_businesses_label',
                      )}
                    </span>
                  )}
                  {partnerDetails.businesses.length > 0 && (
                    <div className="flex min-w-full flex-col items-center justify-center gap-3">
                      {partnerDetails.businesses.map((biz) => {
                        const selected =
                          (!isNil(updatedBusinesses) &&
                            updatedBusinesses.filter(
                              (serviceBiz) => serviceBiz === biz.id,
                            ).length > 0) ||
                          (isNil(updatedBusinesses) &&
                            !isNil(getPartnerServiceDetailsResponse) &&
                            !isNil(getPartnerServiceDetailsResponse.data) &&
                            getPartnerServiceDetailsResponse.data.businesses.filter(
                              (serviceBiz) => serviceBiz.id === biz.id,
                            ).length > 0);

                        return selected ? (
                          <button
                            id="biz-selection-btn"
                            key="biz-selection-btn"
                            aria-label="biz-selection-btn"
                            data-test-id="biz-selection-btn"
                            type="button"
                            onClick={() => onBusinessUncheck(biz.id)}
                            className="group relative flex min-w-full grow cursor-pointer flex-row items-start justify-center gap-3"
                            disabled={onUpdateService}
                          >
                            <span className="flex min-h-full flex-col items-center justify-center py-3 text-sky-600">
                              <MdRadioButtonChecked
                                data-test-id="service-splace-checkbox-checked"
                                size={20}
                              />
                            </span>
                            <span className="flex grow flex-col items-start justify-center rounded-lg border-2 border-sky-600 bg-white p-3 group-hover:bg-white">
                              {biz?.name ? (
                                <span className="font-semibold capitalize">
                                  {biz.name}
                                </span>
                              ) : (
                                <span className="font-semibold capitalize">
                                  {biz.location.address}, {biz.location.city}
                                </span>
                              )}
                              {biz?.name && (
                                <span className="text-left text-sm capitalize">
                                  {biz.location.address}, {biz.location.city}
                                </span>
                              )}
                            </span>
                          </button>
                        ) : (
                          <button
                            id="biz-selection-btn"
                            key="biz-selection-btn"
                            aria-label="biz-selection-btn"
                            data-test-id="biz-selection-btn"
                            type="button"
                            onClick={() => onBusinessCheck(biz.id)}
                            className="group relative flex min-w-full grow cursor-pointer flex-row items-start justify-center gap-3"
                            disabled={onUpdateService}
                          >
                            <span className="flex min-h-full flex-col items-center justify-center py-3 text-slate-300">
                              <MdRadioButtonUnchecked
                                data-test-id="service-splace-checkbox-unchecked"
                                size={20}
                              />
                            </span>
                            <span className="flex grow flex-col items-start justify-center rounded-lg border-2 border-slate-200 bg-white p-3 group-hover:border-slate-400">
                              {biz?.name ? (
                                <span className="font-semibold capitalize">
                                  {biz.name}
                                </span>
                              ) : (
                                <span className="font-semibold capitalize">
                                  {biz.location.address}, {biz.location.city}
                                </span>
                              )}
                              {biz?.name && (
                                <span className="text-left text-sm capitalize">
                                  {biz.location.address}, {biz.location.city}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </span>
              </>
            ) : (
              <>
                <span className="text-lg font-bold drop-shadow-sm">
                  {t(
                    'static.dashboard.service_details_section.businesses_label',
                  )}
                </span>
                <span className="flex flex-col items-start justify-start gap-3 text-lg">
                  {serviceDetails.businesses.length > 0 && (
                    <ul className="list-disc ps-4">
                      {serviceDetails.businesses.map((biz) => (
                        <li className="text-lg font-semibold capitalize">
                          {biz?.name
                            ? biz.name
                            : `${biz.location.address}, ${biz.location.city}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  {serviceDetails.businesses.length === 0 && (
                    <span className="min-w-full text-center font-semibold text-slate-500">
                      {t(
                        'static.dashboard.service_details_section.no_businesses_label',
                      )}
                    </span>
                  )}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="min-w-full grow">&nbsp;</div>

        {isDetailsOnEdit ? (
          <div className="flex min-w-full flex-col items-center justify-center gap-3 sticky bottom-0 bg-white p-3">
            <Button
              disabled={onUpdateService}
              loading={onUpdateService}
              label={t(
                'static.dashboard.service_details_section.button_label_save',
              )}
              onClick={handleSubmit(onSave)}
              data-test-id="service-details-save-btn-action"
              id={`service-details-save-btn-${serviceDetails.id}`}
            />
            <Button
              disabled={onUpdateService}
              loading={false}
              label={t(
                'static.dashboard.service_details_section.button_label_cancel',
              )}
              onClick={onCancel}
              data-test-id="service-details-cancel-btn-action"
              id={`service-details-cancel-btn-${serviceDetails.id}`}
              outline
            />
          </div>
        ) : (
          <div className="hidden min-w-full flex-col items-center justify-center gap-3">
            <Button
              disabled={onSwitchService || onDeleteService}
              loading={false}
              label={t(
                'static.dashboard.service_details_section.button_label_modify',
              )}
              onClick={onModify}
              data-test-id="service-details-modify-btn-action"
              id={`service-details-modify-btn-${serviceDetails.id}`}
              outline
              icon={MdOutlineEdit}
            />
            {serviceDetails.active ? (
              <Button
                disabled={onSwitchService || onDeleteService}
                loading={onSwitchService}
                label={t(
                  'static.dashboard.service_details_section.button_label_disable',
                )}
                onClick={() => onDisableCall(true)}
                data-test-id="service-details-disable-btn-action"
                id={`service-details-disable-btn-${serviceDetails.id}`}
                outline
                icon={LuEyeOff}
              />
            ) : (
              <Button
                disabled={onSwitchService || onDeleteService}
                loading={onSwitchService}
                label={t(
                  'static.dashboard.service_details_section.button_label_enable',
                )}
                onClick={() => onDisableCall(false)}
                data-test-id="service-details-enable-btn-action"
                id={`service-details-enable-btn-${serviceDetails.id}`}
                outline
                icon={LuEye}
              />
            )}
            <Button
              disabled={onDeleteService || onSwitchService}
              loading={onDeleteService}
              label={t(
                'static.dashboard.service_details_section.button_label_delete',
              )}
              onClick={onDeleteCall}
              data-test-id="service-details-delete-btn-action"
              id={`service-details-delete-btn-${serviceDetails.id}`}
              custom="outcancel"
              icon={MdDeleteOutline}
            />
          </div>
        )}
      </div>
    </DashboardSectionDetailsContainer>
  ) : (
    <DashboardSectionContainer>
      <div className="flex size-full min-w-full flex-col items-center justify-center">
        <div className="flex grow flex-col items-center justify-center">
          <Spinner color="fill-sky-600" />
        </div>
      </div>
    </DashboardSectionContainer>
  );
};

export default ServiceDetailsSection;
